import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productService, GameListFilters } from './productService';
import { Game } from './types';
import { Button } from '@/components/ui/button';
import ResponsiveImage from '@/components/ResponsiveImage';
import { hasActiveDiscount, originalPrice } from './discount';
import SharingPrices from './SharingPrices';

// ─── Константы ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const PLATFORMS = ['PS4', 'PS5', 'Xbox One', 'Xbox Series X/S', 'Steam', 'Nintendo Switch', 'Battle.Net', 'Ubisoft', 'Roblox', 'PUBG', 'Minecraft'];

const GENRES = [
  'Action', 'RPG', 'Strategy', 'Adventure', 'Shooter',
  'Sports', 'Racing', 'Simulation', 'Horror', 'Puzzle',
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '',                label: 'По умолчанию' },
  { value: 'price_asc',      label: 'Цена ↑' },
  { value: 'price_desc',     label: 'Цена ↓' },
  { value: 'name_asc',       label: 'Название A–Я' },
  { value: 'name_desc',      label: 'Название Я–А' },
  { value: 'dateRelease_desc', label: 'Сначала новые' },
  { value: 'dateRelease_asc',  label: 'Сначала старые' },
];

// ─── Хелперы ─────────────────────────────────────────────────────────────────

const fmt = (price: number) => new Intl.NumberFormat('ru-RU').format(price);
function parseSortOption(val: string): Pick<GameListFilters, 'sortBy' | 'sortDesc'> {
  if (!val) return {};
  const [field, dir] = val.split('_');
  return { sortBy: field as GameListFilters['sortBy'], sortDesc: dir === 'desc' };
}

function buildSortOption(sortBy?: string, sortDesc?: boolean): string {
  if (!sortBy) return '';
  return `${sortBy}_${sortDesc ? 'desc' : 'asc'}`;
}

// ─── Компоненты ───────────────────────────────────────────────────────────────

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    onClick={onClick}
    variant="ghost"
    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
      active
        ? 'border-red-500 bg-red-600/20 text-red-600 dark:text-red-400'
        : 'border-foreground/10 bg-foreground/5 text-foreground/60 hover:border-foreground/20 hover:text-foreground'
    }`}
  >
    {children}
  </Button>
);

const Toggle = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    onClick={onClick}
    variant="ghost"
    className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
      active
        ? 'border-red-400 bg-red-500/20 text-red-600 dark:text-red-300'
        : 'border-foreground/10 bg-foreground/5 text-foreground/60 hover:border-foreground/20 hover:text-foreground'
    }`}
  >
    <span
      className={`h-2 w-2 rounded-full ${active ? 'bg-red-400' : 'bg-foreground/25'}`}
    />
    {children}
  </Button>
);

const GameCard = ({ game, sharing = false }: { game: Game; sharing?: boolean }) => {
  // Обычная цена и скидка относятся к покупке IN/TR; цены шеринга берём из предложений.
  const discount = !sharing && hasActiveDiscount(game) ? game.discountPercent : null;
  const oldPrice = originalPrice(game);
  return (
  <Link
    to={`/product/${game.id}`}
    className="group overflow-hidden rounded-[28px] border border-foreground/10 bg-card shadow-[0_18px_40px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1 hover:border-foreground/20 hover:shadow-[0_24px_55px_rgba(0,0,0,0.15)]"
  >
    <div className="relative aspect-[5/6] overflow-hidden">
      <ResponsiveImage
        src={game.picture}
        sources={game.pictureSources}
        sizes="(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
        alt={game.name}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
      {game.brandNew && (
        <span className="absolute left-3 top-3 rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
          New
        </span>
      )}
      {game.preOrder && (
        <span className="absolute right-3 top-3 rounded-full bg-red-700 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
          Pre
        </span>
      )}
      {discount && (
        <span className="absolute bottom-3 right-3 rounded-full border border-red-300/40 bg-gradient-to-r from-red-700 to-red-500 px-3 py-1.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(220,38,38,0.45)] backdrop-blur-sm">
          −{discount}%
        </span>
      )}
    </div>
    <div className="p-2.5 sm:p-4">
      {!sharing && <div className="flex flex-wrap items-center gap-2">
        <div className="text-lg font-semibold text-foreground">{fmt(game.price)} ₽</div>
        {oldPrice && (
          <div className="text-xs font-medium text-foreground/40 line-through decoration-red-500/70">
            {fmt(oldPrice)} ₽
          </div>
        )}
        {discount && (
          <span className="rounded-full bg-red-500/15 px-2 py-1 text-[11px] font-bold text-red-600 dark:text-red-400">
            Скидка {discount}%
          </span>
        )}
      </div>}
      <h3 className={`mt-1.5 line-clamp-2 text-sm font-semibold leading-5 text-foreground/85 ${sharing ? 'min-h-[2.5rem]' : ''}`}>
        {game.name}
      </h3>
      {sharing && <SharingPrices game={game} />}
      {game.genres?.length > 0 && (
        <p className="mt-2 truncate text-xs text-foreground/40">{game.genres.slice(0, 2).join(' · ')}</p>
      )}
    </div>
  </Link>
  );
};

const SkeletonCard = () => (
  <div className="animate-pulse overflow-hidden rounded-[28px] border border-foreground/10 bg-card">
    <div className="aspect-[5/6] bg-foreground/5" />
    <div className="p-2.5 sm:p-4">
      <div className="mb-2 h-5 w-20 rounded bg-foreground/5" />
      <div className="h-4 rounded bg-foreground/5" />
      <div className="mt-1 h-4 w-2/3 rounded bg-foreground/5" />
    </div>
  </div>
);

// ─── Основной компонент ───────────────────────────────────────────────────────

const CatalogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Состояние фильтров (синхронизировано с URL)
  const [search, setSearch]         = useState(searchParams.get('name') ?? '');
  const [platforms, setPlatforms]   = useState<string[]>(() => {
    const multi = searchParams.getAll('platforms');
    if (multi.length) return multi;
    // Обратная совместимость со старой одиночной ссылкой ?platform=...
    const single = searchParams.get('platform');
    return single ? [single] : [];
  });
  const [genres, setGenres]         = useState<string[]>(searchParams.getAll('genres'));

  const togglePlatform = (p: string) =>
    setPlatforms(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));
  const [online, setOnline]         = useState<boolean | undefined>(
    searchParams.get('online') === 'true' ? true : searchParams.get('online') === 'false' ? false : undefined,
  );
  const [multiplayer, setMultiplayer] = useState<boolean | undefined>(
    searchParams.get('multiplayer') === 'true' ? true : undefined,
  );
  const [priceMin, setPriceMin]     = useState(searchParams.get('priceMin') ?? '');
  const [priceMax, setPriceMax]     = useState(searchParams.get('priceMax') ?? '');
  const [sortOption, setSortOption] = useState(
    buildSortOption(
      searchParams.get('sortBy') ?? undefined,
      searchParams.get('sortDesc') === 'true',
    ),
  );
  const [preOrder, setPreOrder]     = useState(searchParams.get('preOrder') === 'true');
  const [brandNew, setBrandNew]     = useState(searchParams.get('brandNew') === 'true');
  const [salesLeader, setSalesLeader] = useState(searchParams.get('salesLeader') === 'true');
  const [discounted, setDiscounted] = useState(searchParams.get('discounted') === 'true');
  const [lowestPrice, setLowestPrice] = useState(searchParams.get('lowestPrice') === 'true');
  const [topMetacritic, setTopMetacritic] = useState(searchParams.get('topMetacritic') === 'true');
  const [sharing, setSharing]       = useState(searchParams.get('sharing') === 'true');

  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSort(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Результаты
  const [games, setGames]       = useState<Game[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── Построение объекта фильтров из состояния ────────────────────────────────
  const buildFilters = useCallback(
    (pageNum: number): GameListFilters => {
      const sort = parseSortOption(sortOption);
      return {
        name: search || undefined,
        platforms: platforms.length ? platforms : undefined,
        genres: genres.length ? genres : undefined,
        online,
        multiplayer,
        priceMin: priceMin ? Number(priceMin) : undefined,
        priceMax: priceMax ? Number(priceMax) : undefined,
        preOrder: preOrder || undefined,
        brandNew: brandNew || undefined,
        salesLeader: salesLeader || undefined,
        discounted: discounted || undefined,
        lowestPrice: lowestPrice || undefined,
        topMetacritic: topMetacritic || undefined,
        sharing: sharing || undefined,
        ...sort,
        pageNumber: pageNum,
        pageSize: PAGE_SIZE,
      };
    },
    [search, platforms, genres, online, multiplayer, priceMin, priceMax, sortOption, preOrder, brandNew, salesLeader, discounted, lowestPrice, topMetacritic, sharing],
  );

  // ── Синхронизация состояния → URL ───────────────────────────────────────────
  const syncUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (search)       params.set('name', search);
    platforms.forEach(p => params.append('platforms', p));
    genres.forEach(g => params.append('genres', g));
    if (online !== undefined) params.set('online', String(online));
    if (multiplayer)  params.set('multiplayer', 'true');
    if (priceMin)     params.set('priceMin', priceMin);
    if (priceMax)     params.set('priceMax', priceMax);
    if (sortOption)   {
      const s = parseSortOption(sortOption);
      if (s.sortBy) params.set('sortBy', s.sortBy);
      if (s.sortDesc) params.set('sortDesc', 'true');
    }
    if (preOrder)     params.set('preOrder', 'true');
    if (brandNew)     params.set('brandNew', 'true');
    if (salesLeader)  params.set('salesLeader', 'true');
    if (discounted) params.set('discounted', 'true');
    if (lowestPrice) params.set('lowestPrice', 'true');
    if (topMetacritic) params.set('topMetacritic', 'true');
    if (sharing)      params.set('sharing', 'true');
    setSearchParams(params, { replace: true });
  }, [search, platforms, genres, online, multiplayer, priceMin, priceMax, sortOption, preOrder, brandNew, salesLeader, discounted, lowestPrice, topMetacritic, sharing, setSearchParams]);

  // ── Загрузка данных ─────────────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (pageNum: number, append = false) => {
      try {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);
        setError(null);

        const res = await productService.fetchProducts(buildFilters(pageNum));
        setGames(prev => (append ? [...prev, ...(res.products as Game[])] : (res.products as Game[])));
        setTotal(res.total);
        setPage(pageNum);
      } catch {
        setError('Не удалось загрузить каталог');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildFilters],
  );

  // При изменении фильтров — дебаунс + сброс на страницу 1
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      syncUrl();
      fetchPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [buildFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGenre = (g: string) =>
    setGenres(prev => (prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]));

  const resetFilters = () => {
    setSearch(''); setPlatforms([]); setGenres([]); setOnline(undefined);
    setMultiplayer(undefined); setPriceMin(''); setPriceMax('');
    setSortOption(''); setPreOrder(false); setBrandNew(false); setSalesLeader(false); setLowestPrice(false); setDiscounted(false); setTopMetacritic(false); setSharing(false);
  };

  const hasActiveFilters =
    search || platforms.length || genres.length || online !== undefined ||
    multiplayer || priceMin || priceMax || preOrder || brandNew || salesLeader || discounted || lowestPrice || topMetacritic || sharing || sortOption;

  // ── Рендер ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-[90rem]">

        {/* Шапка */}
        <div className="mb-6 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center rounded-full border border-foreground/10 bg-foreground/5 px-4 py-2 text-sm text-foreground/60 transition-colors hover:bg-foreground/10 hover:text-foreground"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Главная
          </Link>
          <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">{sharing ? 'PS Store Sharing' : 'Каталог'}</h1>
          {!loading && (
            <span className="ml-auto text-sm text-foreground/40">{total} игр</span>
          )}
        </div>

        {/* Поиск + сортировка */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <svg
              className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/35"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по названию или жанру…"
              className="w-full rounded-2xl border border-foreground/10 bg-foreground/5 py-3 pl-11 pr-4 text-sm text-foreground placeholder-foreground/30 outline-none transition focus:border-foreground/25 focus:bg-foreground/8"
            />
            {search && (
              <Button
                onClick={() => setSearch('')}
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 h-auto w-auto text-foreground/30 hover:text-foreground/70 hover:bg-transparent"
              >
                ✕
              </Button>
            )}
          </div>

          <div ref={sortRef} className="relative w-full sm:w-52">
            <Button
              onClick={() => setShowSort(v => !v)}
              variant="ghost"
              className="w-full justify-between rounded-2xl border border-foreground/10 bg-foreground/5 px-4 py-3 text-sm text-foreground"
            >
              <span>{SORT_OPTIONS.find(o => o.value === sortOption)?.label ?? 'По умолчанию'}</span>
              <svg
                className={`h-4 w-4 text-foreground/40 transition-transform ${showSort ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
            {showSort && (
              <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-2xl border border-foreground/10 bg-background py-1 shadow-2xl">
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => { setSortOption(o.value); setShowSort(false); }}
                    className={`w-full px-4 py-3 text-left text-sm transition-colors hover:bg-foreground/5 ${
                      sortOption === o.value ? 'text-red-600 dark:text-red-400' : 'text-foreground/70'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={() => setShowFilters(v => !v)}
            variant="ghost"
            className={`flex items-center gap-2 rounded-2xl border px-5 py-3 text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? 'border-red-500/50 bg-red-600/15 text-red-600 dark:text-red-400'
                : 'border-foreground/10 bg-foreground/5 text-foreground/70 hover:bg-foreground/10'
            }`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 4h18M7 12h10M11 20h2" />
            </svg>
            Фильтры
            {hasActiveFilters && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                !
              </span>
            )}
          </Button>
        </div>

        {/* Панель фильтров */}
        {showFilters && (
          <div className="mb-6 rounded-[24px] border border-foreground/10 bg-foreground/[0.03] p-5">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">

              {/* Платформа */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                  Платформа
                </p>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map(p => (
                    <Chip key={p} active={platforms.includes(p)} onClick={() => togglePlatform(p)}>
                      {p}
                    </Chip>
                  ))}
                </div>
              </div>

              {/* Жанры */}
              <div className="sm:col-span-2 lg:col-span-1">
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                  Жанр
                </p>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map(g => (
                    <Chip key={g} active={genres.includes(g)} onClick={() => toggleGenre(g)}>
                      {g}
                    </Chip>
                  ))}
                </div>
              </div>

              {/* Цена */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                  Цена, ₽
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="от"
                    value={priceMin}
                    onChange={e => setPriceMin(e.target.value)}
                    className="min-w-0 w-full rounded-xl border border-foreground/10 bg-foreground/5 px-3 py-2 text-sm text-foreground placeholder-foreground/25 outline-none focus:border-foreground/25"
                  />
                  <span className="text-foreground/30">—</span>
                  <input
                    type="number"
                    placeholder="до"
                    value={priceMax}
                    onChange={e => setPriceMax(e.target.value)}
                    className="min-w-0 w-full rounded-xl border border-foreground/10 bg-foreground/5 px-3 py-2 text-sm text-foreground placeholder-foreground/25 outline-none focus:border-foreground/25"
                  />
                </div>
              </div>

              {/* Режимы */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                  Режим игры
                </p>
                <div className="flex flex-wrap gap-2">
                  <Toggle active={online === true} onClick={() => setOnline(online === true ? undefined : true)}>
                    Онлайн
                  </Toggle>
                  <Toggle active={multiplayer === true} onClick={() => setMultiplayer(multiplayer ? undefined : true)}>
                    Вдвоём на одном экране
                  </Toggle>
                </div>
              </div>

              {/* Категории */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                  Категория
                </p>
                <div className="flex flex-wrap gap-2">
                  <Toggle active={brandNew}    onClick={() => setBrandNew(v => !v)}>Только вышли</Toggle>
                  <Toggle active={preOrder}    onClick={() => setPreOrder(v => !v)}>Предзаказ</Toggle>
                  <Toggle active={salesLeader} onClick={() => setSalesLeader(v => !v)}>Лидеры продаж</Toggle>
                  <Toggle active={discounted} onClick={() => setDiscounted(v => !v)}>Скидки</Toggle>
                  <Toggle active={lowestPrice} onClick={() => setLowestPrice(v => !v)}>Дешевле не было</Toggle>
                  <Toggle active={topMetacritic} onClick={() => setTopMetacritic(v => !v)}>Лучшие по Metacritic</Toggle>
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <Button
                onClick={resetFilters}
                variant="link"
                className="mt-5 h-auto p-0 text-sm text-foreground/40 underline underline-offset-2 hover:text-foreground/70"
              >
                Сбросить все фильтры
              </Button>
            )}
          </div>
        )}

        {/* Результаты */}
        {loading ? (
          <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-5">
            {[...Array(10)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="rounded-[24px] border border-foreground/10 bg-foreground/5 p-10 text-center">
            <p className="mb-4 text-foreground/50">{error}</p>
            <Button onClick={() => fetchPage(1)} variant="destructive" className="rounded-full px-6 py-2.5 text-sm font-medium">
              Повторить
            </Button>
          </div>
        ) : games.length === 0 ? (
          <div className="rounded-[24px] border border-foreground/10 bg-foreground/5 p-10 text-center">
            <p className="text-foreground/40">Ничего не найдено. Попробуйте изменить фильтры.</p>
            {hasActiveFilters && (
              <Button onClick={resetFilters} variant="ghost" className="mt-4 rounded-full border border-foreground/10 px-5 py-2 text-sm text-foreground/60 hover:text-foreground">
                Сбросить фильтры
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
              {games.map(game => <GameCard key={game.id} game={game} sharing={sharing} />)}
            </div>

            {/* Подгрузка */}
            {games.length < total && (
              <div className="mt-10 text-center">
                <Button
                  onClick={() => fetchPage(page + 1, true)}
                  disabled={loadingMore}
                  variant="ghost"
                  className="rounded-full border border-foreground/12 bg-foreground/5 px-8 py-3 text-sm font-medium text-foreground transition-colors hover:bg-foreground/10 disabled:opacity-50"
                >
                  {loadingMore ? 'Загрузка…' : `Показать ещё (${total - games.length})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CatalogPage;
